<script setup lang="ts">
import './assets/style.scss'
import { useRoute, useRouter } from 'vue-router'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useObjectSubTypeModel } from './../model/model.ts'
import {
  useSaveDictionaryEntryValues,
  useSendForm,
  useSyncObjectTypeFields,
} from './../model/api.ts'
import InventObjectTypesFieldsForm from './InventObjectTypesFieldsForm.vue'
import InventObjectTypesPassportUnifiedTabs from './InventObjectTypesPassportUnifiedTabs.vue'
import { InventObjectTypesGroupModal } from '@/features/Inventory/ObjectTypesGroupModal'
import { WObjectTypeSearch } from '@/widgets'
import { useTranslate } from '@/shared/lib'
import { useAuth } from '@/entities/Auth'
import { useLoadRoleObjectTypes } from '@/features/Role/RoleEdit'

const route = useRoute()
const router = useRouter()
const { trs } = useTranslate()
const auth = useAuth()

const valid = ref(false)

const objectTypesId = computed(() => String(route.params.entity_id || ''))
const objectId = computed(() => String(route.params.object_id))
const section = computed(() => route.params.section as string)
const isCreate = computed(
  () => route.params.action === 'create' || !objectTypesId.value,
)

const model = useObjectSubTypeModel()
const { sendForm } = useSendForm(objectId.value)
const { saveValues } = useSaveDictionaryEntryValues()
const { syncFields } = useSyncObjectTypeFields()

const { isFetching, refetch } = model.loadData(objectTypesId, objectId)

/**
 * Карточка правит поля существующего типа. Раздел роли не играет: и справочник,
 * и элемент «Редактора форм» ведут поля одним и тем же списком, и общий PUT
 * типа их не сохраняет ни там, ни там.
 */
const isFieldEditingCard = computed(() => !isCreate.value && Number(objectTypesId.value) > 0)

/**
 * Запись справочника («А1», «ДРЛ») — у неё есть корень-родитель. Поля корня она
 * наследует и задаёт им значения, но может завести и собственные — они
 * принадлежат ей самой.
 */
const isDictionaryEntry = computed(
  () =>
    section.value === 'manuals' &&
    isFieldEditingCard.value &&
    Number(model.data.list_invent_object_type_id || 0) > 0,
)

/**
 * Снимок полей на момент загрузки — база для diff при сохранении. Пока данных
 * нет, остаётся null: пустой список тогда означал бы «все поля удалили».
 *
 * Момент снимка привязан к появлению самой карточки, а не к флагу загрузки:
 * при ответе из кеша запрос не «фетчится», флаг сразу false, и снимок ушёл бы
 * пустым — тогда правка существующего поля считалась бы неизвестной и молча
 * терялась. Пересниматься на каждую правку он не должен, поэтому следим за id.
 */
const initialFields = ref<any[] | null>(null)

watch(
  () => [isFetching.value, model.data.id] as const,
  ([loading, loadedId]) => {
    if (loading || !loadedId) return
    initialFields.value = JSON.parse(JSON.stringify(model.data.fields ?? []))
  },
  { immediate: true },
)

/**
 * Функция отправки формы
 */
const sendHandler = async () => {
  model.data.styles.stroke.width = Number(model.data.styles.stroke.width)
  model.data.group_id = Number(objectId.value)
  model.data.object_type_id = model.data.object_type.id
  model.data.parent_invent_type_id = model.data.parent_invent_type_id || 0

  // Ensure field-type items have valid inventory_type (required by backend)
  const fallbackInventoryType =
    model.data.parent_invent_object_type?.id ?? model.data.id
  if (model.data.passport_grid_template?.length && fallbackInventoryType > 0) {
    model.data.passport_grid_template = model.data.passport_grid_template.map(
      (item: { type?: string; inventory_type?: number; [k: string]: unknown }) => {
        const isField = !item.type || item.type === 'field'
        const needsInventoryType =
          isField && (item.inventory_type == null || item.inventory_type <= 0)
        if (needsInventoryType) {
          return { ...item, inventory_type: fallbackInventoryType }
        }
        return item
      },
    )
  }

  // Поля и значения сохраняются своими ручками и раньше общего PUT: тот везёт
  // имя, стили и свойства, а поля из его тела бэкенд не читает.
  if (isFieldEditingCard.value) {
    if (!valid.value) return

    // Собственные определения — и у корня, и у записи: запись тоже может
    // завести своё поле сверх унаследованных.
    if (!(await syncFields(Number(objectTypesId.value), model.data.fields, initialFields.value)))
      return

    // Значения записи задаются полям корня; собственные поля записи несут своё
    // значение прямо в определении, поэтому второй раз их не сохраняем.
    if (isDictionaryEntry.value) {
      if (!(await saveValues(Number(objectTypesId.value), model.data.fields))) return
    }
  }

  await sendForm(model.data, valid.value)
}

/* Очищаем данные при выходе из компонента */
onBeforeUnmount(() => {
  model.clearData()
})

/* Следим за query и обновляем тип */
watch(
  route.query,
  () => {
    if (route.query.object_type) {
      model.data.object_type.id = Number(route.query.object_type) || null
    }
  },
  { immediate: true },
)

/** Получаем ссылку назад к списку */
const backLink = computed(() => {
  return router.resolve({
    name: 'objects.groups.admin',
    params: {
      ...route.params,
      action: null,
      entity_id: null,
    },
  })
})

/** Parse `is_system` query param into boolean flag */
const parseIsSystemQuery = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '') {
    return false
  }

  const normalized = String(value).toLowerCase()

  if (normalized === '1' || normalized === 'true') {
    return true
  }

  if (normalized === '0' || normalized === 'false') {
    return false
  }

  return Boolean(value)
}

const parseIsSystemValue = (value: unknown): boolean | null => {
  if (value === undefined || value === null || value === '') {
    return null
  }

  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    return value === 1
  }

  const normalized = String(value).toLowerCase()

  if (normalized === '1' || normalized === 'true') {
    return true
  }

  if (normalized === '0' || normalized === 'false') {
    return false
  }

  return Boolean(value)
}

/** WObjectTypeSearch filter: choose by is_system for create/edit */
const wObjectTypeSearchIsSystem = computed<boolean | null>(() => {
  if (isCreate.value) {
    return parseIsSystemQuery(route.query.is_system)
  }

  return parseIsSystemValue(model.data.object_type?.is_system)
})

const isSystemType = computed(() => parseIsSystemValue(model.data.object_type?.is_system) === true)

const objectTypeLabel = computed(() => {
  if (section.value === 'passports-config') {
    return isSystemType.value ? trs('itf.directory_types') : trs('itf.elements')
  }

  return trs('itf.select_directory')
})

/** Show passport tab only in passports-config edit when parent_object_id > 0 */
const showPassportTab = computed(
  () =>
    !isCreate.value &&
    section.value === 'passports-config' &&
    (model.data.parent_invent_object_type?.id ?? 0) > 0,
)

/**
 * Корневой тип (не справочник) с ровно одним ребёнком не может иметь
 * собственных полей — скрываем кнопку «Добавить поле».
 */
const hideAddField = computed(
  () =>
    section.value === 'passports-config' &&
    !isSystemType.value &&
    !(model.data.list_invent_object_type_id > 0) &&
    Number(model.data.children_count) === 1,
)

/** Load object types for the group to compute allowed ids on create */
const objectTypesQuery = useLoadRoleObjectTypes(computed(() => Number(objectId.value) || 0))
const objectTypes = computed(() => {
  const raw = objectTypesQuery.data.value?.data ?? objectTypesQuery.data.value?.payload ?? []
  if (!Array.isArray(raw)) return []
  return raw.map((item: any) => ({ id: Number(item.id), is_system: Boolean(item.is_system) }))
})

/** On create, restrict object type dropdown to types where user has create permission */
const createAllowedTypeIds = computed<number[] | undefined>(() => {
  if (!isCreate.value) return undefined
  const types = objectTypes.value
  if (!types.length) return undefined
  const sectionIsSystem = parseIsSystemQuery(route.query.is_system)
  return types
    .filter((t: any) => {
      if (sectionIsSystem !== null && parseIsSystemValue(t.is_system) !== sectionIsSystem) {
        return false
      }
      return auth.hasTypePermission(t.id, 'create')
    })
    .map((t: any) => t.id)
})

</script>

<template>
  <VForm :disabled="isFetching" class="mt-4" v-model="valid" @submit.prevent="sendHandler">
    <div class="form__sticky">
      <div class="form__grid_4">
        <WObjectTypeSearch
          :label="objectTypeLabel"
          :multiple="false"
          :is-system="wObjectTypeSearchIsSystem"
          :restrict-to-group-object-types="section !== 'manuals'"
          :allowed-ids="isCreate ? createAllowedTypeIds : undefined"
          v-model="model.data.object_type.id"
        />

        <VTextField
          :label="$t('itf.name_label')"
          v-model="model.data.name"
          :rules="model.rules.name"
        />

        <div class="form__btn_left">
          <VBtn type="submit">{{ $t('itf.save') }}</VBtn>

          <VBtn :to="backLink" color="default" variant="tonal">
            {{ $t('itf.back_list') }}
          </VBtn>
        </div>
      </div>
    </div>
  </VForm>

  <div v-if="isFetching" class="d-flex justify-center align-center py-16">
    <VProgressCircular indeterminate color="primary" size="48" />
  </div>

  <template v-else>
    <InventObjectTypesPassportUnifiedTabs
      v-if="showPassportTab"
      v-model:fields="model.data.fields"
      v-model:passport-grid-template="model.data.passport_grid_template"
      :refetch="refetch"
      :groups="model.data.parent_invent_object_type?.field_groups || model.data.field_groups"
      :is-child="model.data.list_invent_object_type_id > 0"
      :parent-type="model.data.parent_invent_object_type"
      :hide-add-field="hideAddField"
    />

    <template v-if="!showPassportTab">
      <InventObjectTypesFieldsForm
        :refetch
        :hide-secondary-groups="section === 'passports-config' && !showPassportTab"
        :groups="model.data.parent_invent_object_type?.field_groups || model.data.field_groups"
        :is-child="model.data.list_invent_object_type_id > 0"
        :is-system="model.data.object_type.is_system"
        :parent-type="model.data.parent_invent_object_type"
        :hide-add-field="hideAddField"
        v-model="model.data.fields"
      />
    </template>
  </template>

  <InventObjectTypesGroupModal
    v-if="!showPassportTab"
    :is-system="model.data.object_type?.is_system"
    @added="refetch"
  />
</template>
